import { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Formik, useFormikContext } from 'formik'
import { Button, CrossIcon, SearchIcon } from '@farewill/ui'
import { BORDER, COLOR, FONT, GTR } from '@farewill/ui/tokens'
import get from 'lodash/get'
import set from 'lodash/set'

import useDebounce from 'lib/effects/debounce'
import useApi from 'lib/effects/api'

import {
  FloatingWrapper,
  FloatingLabel,
  FloatingInput,
} from 'components/styled/helpers/floating'
import { getFieldState } from 'components/form/helpers'
import { Crematorium } from 'lib/models/crematorium'

const ResultsList = styled.ul`
  position: absolute;
  top: 58px;
  width: 100%;
  border: 1px solid ${COLOR.GREY.LIGHT};
  background-color: ${COLOR.WHITE};
  border-radius: ${BORDER.RADIUS.S};
  box-shadow: ${BORDER.SHADOW.M};
  padding: 0;
  max-height: 400px;
  overflow: auto;
  z-index: 1;

  &:before {
    content: '';
    display: block;
    height: 1px;
    background-color: ${COLOR.GREY.LIGHT};
    width: calc(100% - ${GTR.L});
    position: absolute;
    top: 0;
    left: ${GTR.S};
  }
`

const ResultLink = styled.li`
  padding: ${GTR.XS} ${GTR.S};
  width: 100%;
  text-decoration: none;

  &:hover,
  &:focus {
    background-color: ${COLOR.BACKGROUND.FOG};
    cursor: pointer;
    text-decoration: none;
  }
`

const SearchLoadingMessage = styled.li`
  padding: ${GTR.XS} ${GTR.S};
  width: 100%;
  text-decoration: none;
`

const ResultName = styled.div`
  font-weight: ${FONT.WEIGHT.MEDIUM};
  font-size: ${FONT.SIZE.M};
`
const ResultAddress = styled.div`
  font-weight: ${FONT.WEIGHT.REGULAR};
  font-size: ${FONT.SIZE.M};
`
const ResultDistance = styled.div`
  font-weight: ${FONT.WEIGHT.REGULAR};
  font-size: ${FONT.SIZE.M};
  color: ${COLOR.GREY.MEDIUM};
`

const StyledSearchIcon = styled(SearchIcon)`
  position: absolute;
  left: ${GTR.S};
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
`

const IndentedFloatingInput = styled(FloatingInput)`
  padding-left: ${GTR.XL};
  padding-right: ${GTR.XL};
`

const IndentedFloatingLabel = styled(FloatingLabel)`
  left: ${GTR.XL};
`

const ClearSearchButton = styled(Button.Plain)`
  position: absolute;
  right: 0;
  height: 100%;
  top: 0;

  &:not([disabled]):focus,
  &:not([disabled]):active {
    box-shadow: 0 0 0 2px ${COLOR.GREY.LIGHT};
  }
`

const NEAREST_RESULTS_LIMIT = 5
const MIN_SEARCH_LENGTH = 2

type CrematoriumResponse = {
  id: number
  attributes: { distanceMiles?: number; name: string; address: string }
}

const CrematoriumForm = ({
  onCrematoriumChanged,
  label,
  name,
  searchValue,
  searchTrigger,
}: {
  onCrematoriumChanged: (crematorium: Crematorium) => void
  label: string
  name: string
  searchValue?: string
  searchTrigger?: number
}) => {
  const formik = useFormikContext()
  const { handleChange, setFieldValue, values } = formik

  const searchEl = useRef<HTMLDivElement>(null)

  const [focused, setFocused] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery, (searchString) =>
    searchString.length > 2 ? 300 : 600
  )

  const [
    { data: searchResponse, isLoading },
    makeRequest,
    resetSearchResponse,
  ] = useApi<CrematoriumResponse[]>()

  useEffect(() => {
    setFieldValue(name, {
      search: '',
    })
  }, [setFieldValue, name])

  useEffect(() => {
    if (searchValue) {
      setFieldValue(name, { search: searchValue })
      setFocused(true)
      setSearchQuery(searchValue)
    }
  }, [searchValue, searchTrigger, setFieldValue, name])

  useEffect(() => {
    if (
      debouncedSearchQuery &&
      debouncedSearchQuery.trim().length >= MIN_SEARCH_LENGTH
    ) {
      makeRequest({
        url: '/api/crematoria/nearest',
        method: 'POST',
        data: {
          data: {
            type: 'crematoria',
            attributes: {
              location: debouncedSearchQuery,
              limit: NEAREST_RESULTS_LIMIT,
            },
          },
        },
      })
    } else {
      resetSearchResponse()
    }
  }, [debouncedSearchQuery, makeRequest, resetSearchResponse])

  useEffect(() => {
    const handleCloseClick = (e: MouseEvent) => {
      if (
        focused &&
        searchEl?.current &&
        e.target instanceof Node &&
        !searchEl.current.contains(e.target)
      ) {
        setFocused(false)
      }
    }

    document.addEventListener('click', handleCloseClick)
    return () => document.removeEventListener('click', handleCloseClick)
  }, [focused])

  const onSearchResultClick = ({ result }: { result: CrematoriumResponse }) => {
    setFieldValue(name, {
      search: '',
    })
    onCrematoriumChanged({
      id: result.id,
      name: result.attributes.name,
      address: result.attributes.address,
      distance: result.attributes.distanceMiles,
    })
  }

  const showResultsList = focused && get(values, name).search
  const hasCurrentSearchResults = !isLoading && searchResponse
  const { hasValue, hasHighlight } = getFieldState({
    formik,
    name: `${name}.search`,
  })

  return (
    <div ref={searchEl}>
      <FloatingWrapper highlight={hasHighlight}>
        <StyledSearchIcon color={COLOR.GREY.MEDIUM} />
        <IndentedFloatingInput
          highlight={hasHighlight}
          id={`${name}.search`}
          name={`${name}.search`}
          onFocus={() => setFocused(true)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            handleChange(e)
            setFocused(true)
            setSearchQuery(e.target.value)
          }}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
              e.preventDefault()
            }
          }}
        />

        {!!hasValue && (
          <ClearSearchButton
            type="button"
            padding={0}
            margin={0}
            aria-label="Clear search"
            onClick={() => {
              setFieldValue(name, { search: '' })
              setSearchQuery('')
              resetSearchResponse()
            }}
          >
            <CrossIcon />
          </ClearSearchButton>
        )}

        {showResultsList && (
          <ResultsList data-testid="crematorium-results-list">
            {isLoading && (
              <SearchLoadingMessage>Loading...</SearchLoadingMessage>
            )}

            {hasCurrentSearchResults &&
              searchResponse.map((result) => (
                <ResultLink
                  key={result.id}
                  onClick={() => {
                    onSearchResultClick({ result })
                  }}
                >
                  <ResultName>{result.attributes.name}</ResultName>
                  <ResultAddress>{result.attributes.address}</ResultAddress>
                  {result.attributes.distanceMiles !== undefined && (
                    <ResultDistance color={COLOR.GREY.MEDIUM}>
                      {result.attributes.distanceMiles} miles away
                    </ResultDistance>
                  )}
                </ResultLink>
              ))}
          </ResultsList>
        )}

        <IndentedFloatingLabel
          htmlFor={`${name}.search`}
          full={hasValue || focused}
        >
          {label}
        </IndentedFloatingLabel>
      </FloatingWrapper>
    </div>
  )
}

export type CrematoriumInputProps = {
  onCrematoriumChanged: (crematorium: Crematorium) => void
  label?: string
  name: string
  searchValue?: string
  searchTrigger?: number
}

const CrematoriumInput = ({
  onCrematoriumChanged,
  label = 'Enter town or postcode',
  name,
  searchValue,
  searchTrigger,
}: CrematoriumInputProps) => {
  const { handleSubmit } = useFormikContext()

  return (
    <Formik
      initialValues={set({}, name, { search: '' })}
      onSubmit={() => handleSubmit()}
    >
      <CrematoriumForm
        onCrematoriumChanged={onCrematoriumChanged}
        label={label}
        searchValue={searchValue}
        searchTrigger={searchTrigger}
        name={name}
      />
    </Formik>
  )
}

export default CrematoriumInput
